import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Select,
  MenuItem,
  TextField,
  Paper,
  Button,
  Dialog,
  DialogContent
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import Search from "./ProductData/Search";
import ProductForm from "./ProductData/ProductForm";
import { useParams, useLocation } from "react-router-dom";

export default function ProductData({ onProductDataChange }) {
  const searchRef = useRef();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rows, setRows] = useState([]);
  const [descripcion, setDescripcion] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [imagenDialog, setImagenDialog] = useState("");
  const { id } = useParams();
  const location = useLocation();
  const isEdit = location.pathname.includes("/editar/");
  const API_TOKEN = process.env.REACT_APP_API_TOKEN || "";

  const normalizeDecimalInput = (raw) => {
    let s = String(raw ?? "");
    s = s.replace(/,/g, ".");
    s = s.replace(/[^\d.]/g, "");
    const firstDot = s.indexOf(".");
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
    }
    return s;
  };

  const normalizeIntegerInput = (raw) => {
    let s = String(raw ?? "");
    s = s.replace(/[^\d]/g, "");
    return s;
  };

  const toNum = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const n = Number(String(v).replace(/,/g, "."));
    return Number.isFinite(n) ? n : 0;
  };

  const centsToNumber = (c) => Number((c / 100).toFixed(2));

  const calcRow = (r) => {
    const qty = r.cantidad === "" ? 0 : toNum(r.cantidad);
    const price = r.pvp === "" ? 0 : toNum(r.pvp);
    const dPct = r.descuentoPct === "" ? 0 : toNum(r.descuentoPct);
    const ivaPct = Number(r.iva_porcentaje ?? 15);

    const brutoC = Math.round(price * qty * 100);
    const descC = Math.round((brutoC * dPct) / 100);
    const netoC = brutoC - descC;

    return {
      qty,
      price,
      dPct,
      ivaPct,
      brutoC,
      descC,
      netoC,
      bruto: centsToNumber(brutoC),
      descTotal: centsToNumber(descC),
      neto: centsToNumber(netoC)
    };
  };

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        const { brutoC, descC, netoC, ivaPct } = calcRow(r);

        if (ivaPct === 15) {
          acc.bruto15C += brutoC;
          acc.neto15C += netoC;
        } else if (ivaPct === 5) {
          acc.bruto5C += brutoC;
          acc.neto5C += netoC;
        } else if (ivaPct === 999) {
          acc.brutoNoObjC += brutoC;
          acc.netoNoObjC += netoC;
        } else {
          acc.bruto0C += brutoC;
          acc.neto0C += netoC;
        }

        acc.descuentoC += descC;
        return acc;
      },
      {
        bruto15C: 0,
        bruto5C: 0,
        bruto0C: 0,
        brutoNoObjC: 0,
        neto15C: 0,
        neto5C: 0,
        neto0C: 0,
        netoNoObjC: 0,
        descuentoC: 0
      }
    );
  }, [rows]);

  const subtotalSinImpuestosC =
    totals.neto15C + totals.neto5C + totals.neto0C + totals.netoNoObjC;

  const iva15C = Math.round((totals.neto15C * 15) / 100);
  const iva5C = Math.round((totals.neto5C * 5) / 100);

  const totalC = subtotalSinImpuestosC + iva15C + iva5C;

  const iva15 = centsToNumber(iva15C);
  const iva5 = centsToNumber(iva5C);
  const subtotalSinImpuestos = centsToNumber(subtotalSinImpuestosC);
  const total = centsToNumber(totalC);

  useEffect(() => {
    if (!isEdit || !id) return;

    fetch(`/proformas/detalle-proforma/${id}/`, {
      headers: { "X-API-TOKEN": API_TOKEN }
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (!data?.productos || !Array.isArray(data.productos)) return;

        const precargados = data.productos.map((p) => ({
          codigo_principal: p.codigo_principal || "",
          codigo_auxiliar: p.codigo_auxiliar || "",
          cantidad: String(p.cantidad ?? "1"),
          descripcion: p.descripcion || p.detalle || p.name || "",
          detalles: p.detalles || "",
          pvp: String(Number(p.pvp || 0).toFixed(2)),
          iva_porcentaje: Number(p.iva_porcentaje ?? 15),
          descuentoPct: String(Number(p.descuentoPct || 0)),
          imagen: p.imagen || "",
          is_service: p.is_service || false
        }));

        setRows(precargados);
        setDescripcion(data.descripcion || "");
      })
      .catch(() => {});
  }, [id, isEdit, API_TOKEN]);

  useEffect(() => {
    onProductDataChange({
      productos: rows,
      subtotal15: centsToNumber(totals.neto15C),
      subtotal5: centsToNumber(totals.neto5C),
      subtotal0: centsToNumber(totals.neto0C),
      subtotalNoObj: centsToNumber(totals.netoNoObjC),
      subtotal15_bruto: centsToNumber(totals.bruto15C),
      subtotal5_bruto: centsToNumber(totals.bruto5C),
      subtotal0_bruto: centsToNumber(totals.bruto0C),
      subtotalNoObj_bruto: centsToNumber(totals.brutoNoObjC),
      subtotal_sin_impuestos: subtotalSinImpuestos,
      descuento_total: centsToNumber(totals.descuentoC),
      iva15,
      iva5,
      total,
      descripcion,
      descuentos_ya_aplicados_en_subtotales: true
    });
  }, [
    rows,
    descripcion,
    onProductDataChange,
    totals,
    iva15,
    iva5,
    total,
    subtotalSinImpuestos
  ]);

  const handleAdd = ({
    pvp,
    iva_porcentaje,
    detalles,
    is_service,
    imagen,
    descuentoPct,
    codigo_principal,
    codigo_auxiliar,
    sku
  }) => {
    if (!selectedProduct) {
      setErrorMessage("Debe seleccionar un item.");
      return;
    }

    const codePrincipal =
      codigo_principal ||
      selectedProduct.codigo_principal ||
      (selectedProduct.code && (sku || selectedProduct.sku)
        ? `${selectedProduct.code}-${sku || selectedProduct.sku}`
        : selectedProduct.code || "");

    const aux =
      codigo_auxiliar ||
      selectedProduct.codigo_auxiliar ||
      "";

    const baseName = selectedProduct.name || "";
    const pvpNorm = normalizeDecimalInput(pvp);
    const descNorm = normalizeDecimalInput(descuentoPct);

    setRows((prev) => [
      ...prev,
      {
        codigo_principal: codePrincipal,
        codigo_auxiliar: aux,
        cantidad: "1",
        descripcion: baseName,
        detalles: detalles || "",
        pvp: pvpNorm,
        iva_porcentaje: Number(iva_porcentaje),
        descuentoPct: descNorm === "" ? "0" : String(Number(descNorm)),
        imagen: imagen || "",
        is_service: is_service || false
      }
    ]);

    setSelectedProduct(null);
    searchRef.current?.clear();
    setErrorMessage("");
  };

  const handleDelete = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleFieldChange = (idx, field, value) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;

        if (field === "cantidad") {
          const v = normalizeIntegerInput(value);
          return { ...r, cantidad: v };
        }

        if (field === "descuentoPct") {
          const v = normalizeDecimalInput(value);
          if (v === "" || /^\d*\.?\d*$/.test(v)) return { ...r, descuentoPct: v };
          return r;
        }

        if (field === "pvp") {
          const v = normalizeDecimalInput(value);
          if (v === "" || /^\d*\.?\d*$/.test(v)) return { ...r, pvp: v };
          return r;
        }

        if (field === "iva_porcentaje") return { ...r, iva_porcentaje: Number(value) };

        return { ...r, [field]: value };
      })
    );
  };

  const renderDescripcion = (r) => {
    const desc = (r.descripcion || "").trim();
    const det = (r.detalles || "").trim();
    if (!det) return desc;
    return `${desc}\n- ${det}`;
  };

  const renderCodigo = (r) => {
    const principal = String(r.codigo_principal || "").trim();
    const auxiliar = String(r.codigo_auxiliar || "").trim();
    return auxiliar ? `${principal} / ${auxiliar}` : principal;
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "#1565C0" }}>
        Detalle de Ítem
      </Typography>
      {errorMessage && (
        <Typography color="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
      )}

      <Grid container spacing={2}>
        <Search ref={searchRef} onSelect={setSelectedProduct} />
        <ProductForm producto={selectedProduct} onAdd={handleAdd} />
      </Grid>

      <Box sx={{ mt: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#1565C0" }}>
              <TableCell sx={{ color: "#fff" }}>Código / Auxiliar</TableCell>
              <TableCell sx={{ color: "#fff" }}>Cant</TableCell>
              <TableCell sx={{ color: "#fff" }}>Descripción</TableCell>
              <TableCell sx={{ color: "#fff" }}>P.Unit</TableCell>
              <TableCell sx={{ color: "#fff" }}>IVA (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc (%)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Desc ($)</TableCell>
              <TableCell sx={{ color: "#fff" }}>Subtotal</TableCell>
              <TableCell sx={{ color: "#fff" }}>Imagen</TableCell>
              <TableCell sx={{ color: "#fff" }}>Eliminar</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((r, i) => {
              const { descTotal, neto } = calcRow(r);

              return (
                <TableRow key={i}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {renderCodigo(r)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.cantidad}
                      onChange={(e) =>
                        handleFieldChange(i, "cantidad", e.target.value)
                      }
                      type="text"
                      sx={{ width: 60 }}
                      inputProps={{ inputMode: "numeric" }}
                    />
                  </TableCell>

                  <TableCell sx={{ minWidth: 1 / 0 }}>
                    <TextField
                      value={renderDescripcion(r)}
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={8}
                      InputProps={{ readOnly: true }}
                    />
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.pvp}
                      onChange={(e) => handleFieldChange(i, "pvp", e.target.value)}
                      type="text"
                      sx={{ width: 90 }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </TableCell>

                  <TableCell>
                    <Select
                      value={r.iva_porcentaje}
                      onChange={(e) =>
                        handleFieldChange(i, "iva_porcentaje", e.target.value)
                      }
                      sx={{ width: 90 }}
                    >
                      <MenuItem value={0}>0%</MenuItem>
                      <MenuItem value={5}>5%</MenuItem>
                      <MenuItem value={15}>15%</MenuItem>
                      <MenuItem value={999}>No objeto</MenuItem>
                    </Select>
                  </TableCell>

                  <TableCell>
                    <TextField
                      value={r.descuentoPct}
                      onChange={(e) =>
                        handleFieldChange(i, "descuentoPct", e.target.value)
                      }
                      type="text"
                      sx={{ width: 60 }}
                      inputProps={{ inputMode: "decimal" }}
                    />
                  </TableCell>

                  <TableCell>${Number(descTotal || 0).toFixed(2)}</TableCell>
                  <TableCell>${Number(neto || 0).toFixed(2)}</TableCell>

                  <TableCell>
                    <Button
                      variant="outlined"
                      onClick={() => setImagenDialog(r.imagen || "")}
                      disabled={!r.imagen}
                    >
                      Ver
                    </Button>
                  </TableCell>

                  <TableCell>
                    <IconButton onClick={() => handleDelete(i)}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={8}>
          <Typography sx={{ mb: 1 }}>
            Descripción Factura: <span style={{ color: "red" }}>*</span>
          </Typography>
          <TextField
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            multiline
            minRows={4}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, backgroundColor: "#f8f9fa" }}>
            <Grid container spacing={1}>
              {[
                ["Subtotal 15%", centsToNumber(totals.neto15C)],
                ["Subtotal 5%", centsToNumber(totals.neto5C)],
                ["Subtotal 0%", centsToNumber(totals.neto0C)],
                ["Subtotal No Objeto", centsToNumber(totals.netoNoObjC)],
                ["Descuento", centsToNumber(totals.descuentoC)],
                ["IVA 15%", iva15],
                ["IVA 5%", iva5],
                ["Total", total]
              ].map(([label, value], index) => (
                <React.Fragment key={index}>
                  <Grid item xs={7}>
                    <Typography
                      sx={{
                        color: "#333",
                        fontWeight: label === "Total" ? "bold" : "normal"
                      }}
                    >
                      {label}:
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <TextField
                      value={`$${Number(value || 0).toFixed(2)}`}
                      fullWidth
                      size="small"
                      disabled
                      sx={{
                        "& .Mui-disabled": {
                          backgroundColor: "#f0f0f0 !important",
                          color: "#333 !important",
                          WebkitTextFillColor: "#333 !important"
                        }
                      }}
                    />
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      <Dialog
        open={!!imagenDialog}
        onClose={() => setImagenDialog("")}
        maxWidth="md"
      >
        <DialogContent>
          <Box
            sx={{
              width: 500,
              height: 500,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mx: "auto",
              borderRadius: 2,
              overflow: "hidden"
            }}
          >
            <img
              src={imagenDialog}
              alt="Item"
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain"
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}